import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { generateEvaluatorDocId } from './evaluator/utils';
import { checkAndAutoFinalizeEvaluators } from './evaluator/autoFinalize';

export interface ContextValidationResult {
  eligible: boolean;
  message?: string;
}

export async function validateContextEligibility(
  evaluatorId: string, 
  userId: string
): Promise<ContextValidationResult> {
  try {
    const docId = generateEvaluatorDocId(evaluatorId, userId);
    const evaluatorRef = doc(db, collections.evaluators, docId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      return {
        eligible: false,
        message: 'Evaluator not found'
      };
    }

    const evaluator = evaluatorDoc.data();

    if (evaluator.contextualized) {
      return {
        eligible: false,
        message: 'Context has already been provided for this evaluator'
      };
    }

    // Removed the evaluationCompleted check to allow context after evaluation

    return { eligible: true };
  } catch (error) {
    console.error('Error validating context eligibility:', error);
    throw new Error('Unable to validate context eligibility');
  }
}

export async function updateEvaluatorContext(evaluatorId: string, userId: string): Promise<void> {
  if (!evaluatorId || !userId) throw new Error('Evaluator ID and User ID are required');

  try {
    const docId = generateEvaluatorDocId(evaluatorId, userId);
    const evaluatorRef = doc(db, collections.evaluators, docId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      throw new Error('Evaluator not found');
    }

    await updateDoc(evaluatorRef, {
      contextualized: true,
      updatedAt: new Date()
    });

    // Check for auto-finalization after context update
    await checkAndAutoFinalizeEvaluators(userId);
  } catch (error: any) {
    console.error('Error updating evaluator context:', error);
    throw new Error(error.message || 'Failed to update context');
  }
}